import { type DocumentNode, gql } from '@apollo/client';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import { pick } from 'lodash';
import { CUSTOM, FORMAT, NATIVE } from 'src/constants';
import { assetQuery } from 'src/dynamoQueries/assets';
import { assetNodeTypefromViewModel } from 'src/dynamoQueries/viewModels';
import { allFormatsQuery, formatsQuery } from './formats';
import { allLibMetadataFieldsQuery, MetaQ } from './metadataField';
import type { OpenSearchData, SortingArgsInput, SortingArgsResult, SortOrder, ViewModel } from './types';

interface TransformedEdge {
  node: {
    trExpressions?: unknown[];
    contentObject?: Record<string, unknown>;
    [key: string]: unknown;
  };
}

interface AllItemsResult {
  allItems: {
    edges: TransformedEdge[];
    [key: string]: unknown;
  };
}

export const generateAssetGQLQueryFromViewModel = (id: string, viewModel: ViewModel): DocumentNode => {
  const q = assetQuery();
  const asn = assetNodeTypefromViewModel(viewModel);

  // biome-ignore lint/suspicious/noExplicitAny: Dynamic GraphQL query object merging requires flexible typing - json-to-graphql-query expects specific shapes
  q.query.asset = { ...q.query.asset, ...asn } as any;
  q.query.asset.__args.id = id;
  const gQuery = jsonToGraphQLQuery(q, { pretty: true });
  return gql(gQuery);
};

const addOrderIndicator = (value: string, order: SortOrder): string => {
  return order === 'desc' ? `-${value}` : value;
};

const buildNativeSortArg = (value: string, order: SortOrder): string => {
  return addOrderIndicator(`asset__${value}`, order);
};

const buildFormatSortArg = (value: string, order: SortOrder): string => {
  return addOrderIndicator(`asset__format__${value}`, order);
};

export const buildSortingArgs = ({ value, fieldType, orderBy }: SortingArgsInput): SortingArgsResult => {
  const result: SortingArgsResult = { orderBy: '', customOrder: '' };

  if (fieldType === NATIVE) {
    result.orderBy = buildNativeSortArg(value, orderBy);
  }
  if (fieldType === CUSTOM) {
    result.customOrder = addOrderIndicator(value, orderBy);
  }

  if (fieldType === FORMAT) {
    result.orderBy = buildFormatSortArg(value, orderBy);
  }

  return result;
};

export const convertArrayToRelayArray = <T extends Record<string, unknown>>(arr: T[] = []): Array<{ node: T }> => {
  /* take a normal array and convert it to use nodes */
  return arr?.map((o) => ({ node: { ...o } }));
};

export const openSearchToAllItems = (
  osData: OpenSearchData = { opensearch: {} },
  contentObjectExtras: string[] = [],
): AllItemsResult => {
  /* to be used only temporarily until a better solution is implemented */

  let edges = convertArrayToRelayArray(osData.opensearch.results || []);

  edges = edges?.map((edge) => {
    const extras = pick(edge.node, contentObjectExtras) as Record<string, unknown>;
    const { trExpressions } = extras as { trExpressions?: string };
    try {
      const parsed = trExpressions ? JSON.parse(trExpressions) : [];
      extras.trExpressions = Array.isArray(parsed) ? parsed : [];
    } catch {
      extras.trExpressions = [];
    }
    const { highlights } = extras as { highlights?: string };
    try {
      const parsed = highlights ? JSON.parse(highlights as string) : {};
      extras.highlights = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      extras.highlights = {};
    }
    const { highlightLabels } = extras as { highlightLabels?: string };
    try {
      const parsed = highlightLabels ? JSON.parse(highlightLabels as string) : {};
      extras.highlightLabels = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      extras.highlightLabels = {};
    }
    return {
      node: {
        ...edge.node,
        contentObject: { ...(edge.node.contentObject as Record<string, unknown>), ...extras },
      },
    };
  });

  const result: AllItemsResult = {
    allItems: {
      ...osData.opensearch,
      edges: edges as TransformedEdge[],
    },
  };

  delete (result.allItems as Record<string, unknown>).results;
  return result;
};

export const buildAllMFQ = (): DocumentNode => {
  const query = allLibMetadataFieldsQuery(true);
  const gQuery = jsonToGraphQLQuery(query, { pretty: true });
  return gql(gQuery);
};

export const buildMetaQ = (): DocumentNode => {
  const query = MetaQ(true);
  const gQuery = jsonToGraphQLQuery(query, { pretty: true });
  return gql(gQuery);
};

export const buildSharedAllMFQ = (): DocumentNode => {
  const query = allLibMetadataFieldsQuery(true);

  interface SharedMetadataFieldsQuery {
    query: {
      __variables: typeof query.query.__variables;
      sharedMetadataFields: typeof query.query.allLibMetadataFields;
    };
  }

  const modifiedQuery: SharedMetadataFieldsQuery = {
    query: {
      __variables: query.query.__variables,
      sharedMetadataFields: {
        ...query.query.allLibMetadataFields,
      },
    },
  };

  const gQuery = jsonToGraphQLQuery(modifiedQuery, { pretty: true });
  return gql(gQuery);
};

export const buildSharedMeta = (): DocumentNode => {
  const query = MetaQ(true);

  interface SharedMetaQuery {
    query: {
      __variables: typeof query.query.__variables;
      sharedMeta: typeof query.query.meta;
    };
  }

  const modifiedQuery: SharedMetaQuery = {
    query: {
      __variables: query.query.__variables,
      sharedMeta: {
        ...query.query.meta,
      },
    },
  };

  const gQuery = jsonToGraphQLQuery(modifiedQuery, { pretty: true });
  return gql(gQuery);
};

export const buildAllFieldsQ = (): DocumentNode => {
  const query = allFormatsQuery(true);
  const gQuery = jsonToGraphQLQuery(query, { pretty: true });
  return gql(gQuery);
};

export const buildFormatsQ = (): DocumentNode => {
  const query = formatsQuery(true);
  const gQuery = jsonToGraphQLQuery(query, { pretty: true });
  return gql(gQuery);
};

export const buildSharedFormatsQ = (): DocumentNode => {
  const query = formatsQuery(true);

  interface SharedFormatsQuery {
    query: {
      __variables: typeof query.query.__variables;
      sharedFormats: typeof query.query.formats;
    };
  }

  const modifiedQuery: SharedFormatsQuery = {
    query: {
      __variables: query.query.__variables,
      sharedFormats: {
        ...query.query.formats,
      },
    },
  };

  const gQuery = jsonToGraphQLQuery(modifiedQuery, { pretty: true });
  return gql(gQuery);
};
